import { CURRENT_SCHEMA_VERSION } from './constants.js';
import {
  assertValidPageCount,
  calculateSpineWidthMm,
  getProductProfile,
  SQUARE_200_PRODUCT,
} from './product.js';
import type { CoverDocument, PageDocument, PhotobookProject, ProjectSettings } from './schemas.js';

function uuid(): string {
  return crypto.randomUUID();
}

function nowIso(): string {
  return new Date().toISOString();
}

export function createEmptyPage(order: number): PageDocument {
  return {
    id: uuid(),
    schemaVersion: CURRENT_SCHEMA_VERSION,
    order,
    background: { type: 'color', color: '#ffffff' },
    elements: [],
    warnings: [],
  };
}

export function createEmptyCover(): CoverDocument {
  return {
    schemaVersion: CURRENT_SCHEMA_VERSION,
    panels: {
      front: {
        id: uuid(),
        panel: 'front',
        background: { type: 'color', color: '#f7f4ef' },
        elements: [],
      },
      back: {
        id: uuid(),
        panel: 'back',
        background: { type: 'color', color: '#f7f4ef' },
        elements: [],
      },
      spine: {
        id: uuid(),
        panel: 'spine',
        background: { type: 'color', color: '#ebe6de' },
        elements: [],
      },
    },
  };
}

export type CreateProjectInput = {
  title: string;
  description?: string;
  productProfileId?: string;
  pageCount?: number;
  ownerMode?: 'local' | 'cloud';
  ownerId?: string;
  settings?: Partial<ProjectSettings>;
};

export function createProject(input: CreateProjectInput): PhotobookProject {
  const profile = getProductProfile(input.productProfileId ?? SQUARE_200_PRODUCT.id);
  const pageCount = input.pageCount ?? profile.pageCount.default;
  assertValidPageCount(pageCount, profile);
  const stamp = nowIso();

  return {
    schemaVersion: CURRENT_SCHEMA_VERSION,
    id: uuid(),
    ownerMode: input.ownerMode ?? 'local',
    ownerId: input.ownerId,
    title: input.title.trim() || 'Naamloos fotoboek',
    description: input.description,
    productProfileId: profile.id,
    createdAt: stamp,
    updatedAt: stamp,
    revision: 1,
    locale: 'nl-NL',
    cover: createEmptyCover(),
    pages: Array.from({ length: pageCount }, (_, i) => createEmptyPage(i)),
    assets: [],
    settings: {
      showBleedGuides: true,
      showSafeZoneGuides: true,
      showTrimGuides: true,
      showGutterGuides: true,
      snapEnabled: true,
      allowLowDpiLocalExport: false,
      paperStockId: 'standard-matte',
      ...input.settings,
    },
  };
}

export function touchProject(project: PhotobookProject): PhotobookProject {
  return {
    ...project,
    updatedAt: nowIso(),
    revision: project.revision + 1,
  };
}

export function getCoverWidthMm(project: PhotobookProject): {
  back: number;
  spine: number;
  front: number;
  total: number;
} {
  const profile = getProductProfile(project.productProfileId);
  const pageW = profile.pageSizeMm.width;
  const spine = calculateSpineWidthMm(project.pages.length, project.settings.paperStockId, profile);
  return {
    back: pageW,
    spine,
    front: pageW,
    total: pageW * 2 + spine,
  };
}
